import React, { useCallback, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { CopyIcon, LinkIcon, LockIcon, SuccessIcon } from '@modernloop/shared/icons';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { useCreateLocation } from 'src/hooks/amplitude/useCreateLocationHeader';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';
import useShortUrlCreate from 'src/hooks/useShortUrlCreate';

export enum CreateType {
  CANDIDATE_AVAILABILITY = 'CANDIDATE_AVAILABILITY',
  SELF_SCHEDULE = 'SELF_SCHEDULE',
}

type ShareDialogProps = {
  longUrl: string;
  content: string;
  onClose: () => void;
  onSendEmail: () => void;
  type?: CreateType;
};

const ShareDialog = ({ longUrl, content, onClose, onSendEmail, type }: ShareDialogProps) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState(false);
  const createLocation = useCreateLocation();
  const { logEvent } = useLogEvent();

  const { data, loading: shortUrlLoading } = useShortUrlCreate(longUrl);
  const loading = shortUrlLoading;

  const handleCopyLinkClick = useCallback(() => {
    navigator.clipboard.writeText(data?.shortUrlCreate?.shortUrl || longUrl);
    logEvent(LoggerEvent.CLIENT_SHARE_DIALOG_COPY_LINK, {
      type,
      location: createLocation,
    });
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 5000);
  }, [data, longUrl, logEvent, type, createLocation]);

  const sxProps = { display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 };

  return (
    <Dialog
      open
      maxWidth="xs"
      dataTestId="share-dialog"
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/scheduling/share_64.png"
          alt={t('hiring-dashboard-common:common.share.shareLinkAlt')}
        />
      }
      title={t('hiring-dashboard-common:common.share.shareLink')}
      subTitle={content}
      onClose={onClose}
      submitOptions={{
        onClick: () => {
          onSendEmail();
        },
        label: t('hiring-dashboard-common:common.share.shareViaEmail'),
      }}
      secondaryOptions={{
        dataTestId: 'share-dialog-copy-link-button',
        onClick: () => {
          handleCopyLinkClick();
        },
        label: copySuccess
          ? t('hiring-dashboard-common:common.share.copied')
          : t('hiring-dashboard-common:common.share.copyLink'),
        startIcon: copySuccess ? <SuccessIcon /> : <CopyIcon />,
      }}
    >
      <Stack spacing={2}>
        <Typography sx={sxProps} fontWeight={400}>
          <LockIcon />
          {t('hiring-dashboard-common:common.share.anyoneCanEdit')}
        </Typography>
        {!loading && (
          <Typography data-testid="share-dialog-url" sx={sxProps} fontWeight={400}>
            <LinkIcon />
            {data?.shortUrlCreate?.shortUrl || longUrl}
          </Typography>
        )}
        {loading && (
          <Box sx={{ flexGrow: 1 }}>
            <CircularProgress size={16} />
          </Box>
        )}
      </Stack>
    </Dialog>
  );
};

export default ShareDialog;
